import { alpha, Components, lighten, Theme } from '@mui/material/styles';

import { ColorPalette } from './theme';

export const DEFAULT_FONT = 'Montserrat';

// Palette colors the `soft` button variant is generated for. `inherit` is
// excluded — it has no `.main` channel and is handled as its own variant below.
const SOFT_BUTTON_COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

// Shape of a single entry in `MuiButton.variants`. Declared locally so this file
// does not depend on the `soft` module augmentation being loaded — see the note
// on the `variants` array below.
type SoftButtonVariant = {
  props: Record<string, unknown>;
  style: (arg: { theme: Theme }) => Record<string, unknown>;
};

export const componentsOverride = (palette: ColorPalette, font?: string): Components<Omit<Theme, 'components'>> => {
  const fontFamily = `${font || DEFAULT_FONT}, Arial, sans-serif !important`;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          fontFamily,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: lighten(palette.primary.main, 0.92),
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundColor: '#fafafa',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
        // MUI offsets the action slot with `padding: 4px 0 0 16px` and pins it to
        // `flex-start`, which leaves the button sitting low against the message.
        // Drop the top padding and center it instead.
        action: {
          alignItems: 'center',
          paddingTop: 0,
        },
        // The `standard` variant paints a pale tint of the palette color, so its
        // text must be the *dark* shade — not `contrastText`, which is meant for
        // text sitting on the solid fill (it is `#fff` in the fit palette, i.e.
        // white on pale blue).
        standardInfo: {
          color: palette.info.dark,
          '& .MuiAlert-icon': {
            color: palette.info.dark,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '1rem',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          marginBottom: '4px',
          borderRadius: '4px',
          '&.Mui-selected': {
            backgroundColor: lighten(palette.primary.main, 0.8),
            borderColor: palette.primary.main,
          },
          '&:hover': {
            borderColor: lighten(palette.primary.main, 0.3),
            backgroundColor: lighten(palette.primary.main, 0.7),
          },
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          padding: '15px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained' as const,
        color: 'primary' as const,
      },
      // `soft` — tinted background, no border, colored label. Sits between `text`
      // and `contained` in emphasis. Declared per-color because MUI v5 matches
      // variants on exact prop values, not on `color` generically.
      //
      // Typed as `SoftButtonVariant[]` rather than relying on the `soft` module
      // augmentation: consuming apps compile this file inside their own program,
      // where the sibling `mui-augmentation.d.ts` is not included, so `variant:
      // 'soft'` would not typecheck there. See that file for the full story.
      variants: [
        ...SOFT_BUTTON_COLORS.map(
          (color): SoftButtonVariant => ({
            props: { variant: 'soft', color },
            style: ({ theme }: { theme: Theme }) => ({
              border: 0,
              color: theme.palette[color].main,
              backgroundColor: alpha(theme.palette[color].main, 0.12),
              '&:hover': {
                backgroundColor: alpha(theme.palette[color].main, 0.22),
              },
              '&.Mui-disabled': {
                border: 0,
                color: theme.palette.grey[400],
                background: theme.palette.action.disabledBackground,
              },
            }),
          }),
        ),
        {
          props: { variant: 'soft', color: 'inherit' },
          style: ({ theme }: { theme: Theme }) => ({
            border: 0,
            color: theme.palette.grey[800],
            backgroundColor: theme.palette.grey[200],
            '&:hover': {
              backgroundColor: theme.palette.grey[300],
            },
            '&.Mui-disabled': {
              border: 0,
              color: theme.palette.grey[400],
              background: theme.palette.action.disabledBackground,
            },
          }),
        },
      ] as SoftButtonVariant[] as NonNullable<Components<Omit<Theme, 'components'>>['MuiButton']>['variants'],
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.contrastText,
          borderRadius: '12px',
          px: 3,
          paddingTop: '2px',
          paddingBottom: '2px',
          textTransform: 'none',
          justifyContent: 'space-between',
          minWidth: 'auto',
          minHeight: '40px',
          '&.MuiButton-containedPrimary': {
            background: `linear-gradient(95.35deg, ${theme.palette.primary.main} 11.81%, ${theme.palette.primary.light} 84.7%)`,
          },
          '&.MuiButton-containedInfo': {
            background: `linear-gradient(95.35deg, ${theme.palette.info.main} 11.81%, ${theme.palette.info.light} 84.7%)`,
          },
          '&.MuiButton-outlinedPrimary': {
            color: `${theme.palette.primary.main}`,
          },
          '&.MuiButton-outlinedSecondary': {
            color: `${theme.palette.secondary.main}`,
          },
          '&.MuiButton-outlinedSuccess': {
            color: `${theme.palette.success.main}`,
          },
          '&.MuiButton-outlinedWarning': {
            color: `${theme.palette.warning.main}`,
          },
          '&.MuiButton-outlinedInfo': {
            color: `${theme.palette.info.main}`,
          },
          '&.MuiButton-outlinedError': {
            color: `${theme.palette.primary.main}`,
          },
          '&.MuiButton-outlinedInherit': {
            color: theme.palette.grey[600],
            borderColor: theme.palette.grey[300],
            '&:hover': {
              borderColor: theme.palette.grey[400],
              backgroundColor: theme.palette.grey[100],
            },
          },
          '&.MuiButton-text': {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
          '&.MuiButton-textPrimary': {
            color: theme.palette.primary.main,
            '&:hover': { backgroundColor: `${theme.palette.primary.main}14` },
          },
          '&.MuiButton-textSecondary': {
            color: theme.palette.secondary.main,
            '&:hover': { backgroundColor: `${theme.palette.secondary.main}14` },
          },
          '&.MuiButton-textError': {
            color: theme.palette.error.main,
            '&:hover': { backgroundColor: `${theme.palette.error.main}14` },
          },
          '&.MuiButton-textSuccess': {
            color: theme.palette.success.main,
            '&:hover': { backgroundColor: `${theme.palette.success.main}14` },
          },
          '&.MuiButton-textInherit': {
            color: theme.palette.grey[800],
            '&:hover': { backgroundColor: theme.palette.grey[100] },
          },
          // `background` shorthand, not `backgroundColor` — the contained variants above
          // set a gradient via the shorthand, which MUI's own disabled styling cannot reset.
          // Must stay after those rules: equal specificity, so source order decides.
          '&.Mui-disabled': {
            border: 0,
            color: theme.palette.grey[400],
            background: theme.palette.action.disabledBackground,
          },
          '&.MuiButton-sizeLarge': {
            height: '50px',
          },
        }),
        endIcon: {
          marginRight: '-14px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-colorSuccess': {
            backgroundColor: lighten(palette.success.main, 0.88),
            color: palette.success.dark,
          },
          '&.MuiChip-colorError': {
            backgroundColor: lighten(palette.error.main, 0.88),
            color: palette.error.dark,
          },
          '&.MuiChip-colorWarning': {
            backgroundColor: lighten(palette.warning.main, 0.88),
            color: palette.warning.dark,
          },
          '&.MuiChip-colorInfo': {
            backgroundColor: lighten(palette.info.light, 0.7),
            color: palette.info.dark,
          },
          '&.MuiChip-colorPrimary': {
            backgroundColor: lighten(palette.primary.main, 0.88),
            color: palette.primary.dark,
          },
          '&.MuiChip-outlinedSuccess': {
            color: palette.success.main,
          },
          '&.MuiChip-outlinedError': {
            color: palette.error.main,
          },
          '&.MuiChip-outlinedWarning': {
            color: palette.warning.main,
          },
          '&.MuiChip-outlinedInfo': {
            color: palette.info.main,
          },
          '&.MuiChip-outlinedPrimary': {
            color: palette.primary.main,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          paddingTop: '2px',
          width: '100%',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.MuiOutlinedInput-root': {
            borderRadius: '20px',
          },
          '&.MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
            paddingRight: '9px',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          '&.MuiOutlinedInput-root': {
            padding: '9px 22px !important',
          },
        },
        root: {
          marginTop: '10px',
          borderRadius: 20,
          '& .MuiFormLabel-root': {
            padding: '6px 9px',
          },
          '& .MuiInputLabel-root': {
            top: '-3px',
            left: '-7px',
          },
          '& .MuiTextField-root': {
            paddingTop: `0 !important`,
          },
        },
        // Rounded, softly-shadowed dropdown surface.
        paper: {
          borderRadius: '20px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        },
        // Grouped option headers: light primary background with primary.dark text.
        groupLabel: ({ theme }) => ({
          fontWeight: 700,
          fontSize: '1rem',
          color: theme.palette.primary.dark,
          backgroundColor: lighten(theme.palette.primary.main, 0.9),
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          top: '4px',
          '&:not(.MuiInputLabel-shrink)': {
            left: '12px',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          backgroundColor: palette.primary.main,
          color: palette.common.white,
          marginBottom: '12px',
          fontSize: '1.2rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
        },
        paperFullScreen: {
          borderRadius: 0,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 16px',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-thumb': {
            background: '#fff',
            boxShadow: '0px 5px 8px 0px rgba(0, 0, 0, 0.25)',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h2: ({ theme }) => ({
          fontSize: '36px',
          fontWeight: '700',
          color: palette.grey[900],
          [theme.breakpoints.down('md')]: {
            fontSize: '30px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '22px',
          },
        }),
        h3: ({ theme }) => ({
          color: palette.grey[800],
          [theme.breakpoints.down('md')]: {
            fontSize: '28px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '20px',
          },
        }),
        h4: ({ theme }) => ({
          color: palette.grey[800],
          [theme.breakpoints.down('md')]: {
            fontSize: '26px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '18px',
          },
        }),
        h5: {
          color: palette.grey[800],
        },
        h6: ({ theme }) => ({
          fontSize: '20px',
          color: palette.grey[800],
          [theme.breakpoints.down('md')]: {
            fontSize: '18px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '16px',
          },
        }),
        body1: ({ theme }) => ({
          fontSize: '18px',
          fontWeight: '600',
          [theme.breakpoints.down('md')]: {
            fontSize: '16px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '14px',
          },
        }),
        body2: ({ theme }) => ({
          fontSize: '15px',
          fontWeight: '400',
          [theme.breakpoints.down('md')]: {
            fontSize: '14px',
          },
          [theme.breakpoints.down(376)]: {
            fontSize: '12px',
          },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #dcdcdc',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: `4px 4px 0 0`,
          textTransform: 'none',
          '&.Mui-selected': {
            backgroundColor: lighten(theme.palette.primary.main, 0.9),
          },
        }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderColor: palette.grey[400],
          color: palette.grey[700],
          '&:hover': {
            backgroundColor: lighten(palette.primary.main, 0.9),
          },
          '&.Mui-selected': {
            color: palette.primary.main,
            backgroundColor: lighten(palette.primary.main, 0.88),
            borderColor: lighten(palette.primary.main, 0.7),
            '&:hover': {
              backgroundColor: lighten(palette.primary.main, 0.8),
            },
          },
          '&.Mui-disabled': {
            color: palette.grey[400],
            borderColor: palette.grey[200],
          },
        },
      },
    },
  };
};
